// Auth state as an external store. The session is read from localStorage, so a signed-in user
// gets straight into the app offline; supabase-js refreshes tokens whenever it is online.
import { useSyncExternalStore } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { closeDb } from '../db/session';

export type AuthState = { status: 'loading' } | { status: 'signed-out' } | { status: 'signed-in'; user: User };

let state: AuthState = { status: 'loading' };
let started = false;
const listeners = new Set<() => void>();

function setState(next: AuthState) {
  // Token refreshes re-emit the same user; keep the existing snapshot so nothing re-renders.
  const sameUser = state.status === 'signed-in' && next.status === 'signed-in' && state.user.id === next.user.id;
  if (sameUser || (state.status === next.status && next.status !== 'signed-in')) return;
  state = next;
  listeners.forEach((listener) => listener());
}

function start() {
  if (started) return;
  started = true;
  void supabase.auth.getSession().then(({ data }) => {
    setState(data.session ? { status: 'signed-in', user: data.session.user } : { status: 'signed-out' });
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    setState(session ? { status: 'signed-in', user: session.user } : { status: 'signed-out' });
  });
}

export function useAuth(): AuthState {
  start();
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => state
  );
}

export function useUser(): User {
  const auth = useAuth();
  if (auth.status !== 'signed-in') throw new Error('useUser() called while signed out.');
  return auth.user;
}

export async function sendSignInCode(email: string) {
  const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
  if (error) throw error;
}

export async function verifySignInCode(email: string, token: string) {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
  if (error) throw error;
}

/**
 * Sign out of this device only: delete this account's local database, then end the local session.
 * Other devices stay signed in.
 */
export async function signOut() {
  await closeDb({ destroy: true });
  await supabase.auth.signOut({ scope: 'local' });
}
